import { useEffect, useState } from "react";
import { Card, Title, Text } from "@tremor/react";
import { apiFetch } from "../api/client";

interface Concepto {
  concepto: string;
  mes_actual: number | null;
  mes_anterior: number | null;
  acumulado: number | null;
  pct_ventas: number | null;
  pct_acumulado: number | null;
  var_mes_pct: number | null;
}

interface ResumenERResponse {
  restaurant_id: string;
  anio: number;
  mm: number;
  mes_actual_label: string;
  mes_anterior_label: string;
  rows: Concepto[];
}

const formatPct = (n: number | null | undefined) =>
  n == null ? "—" : `${n.toFixed(1)}%`;

const formatCurrency = (n: number | null | undefined) =>
  n == null
    ? "—"
    : new Intl.NumberFormat("es-PE", { style: "currency", currency: "PEN", maximumFractionDigits: 0 }).format(n);


function DeltaBadge({ value }: { value: number | null | undefined }) {
  if (value == null) return <span className="text-tremor-content">—</span>;
  const positive = value >= 0;
  const cls = positive ? "text-emerald-600" : "text-red-600";
  return (
    <span className={`inline-flex items-center gap-0.5 font-semibold ${cls}`}>
      {positive ? "▲" : "▼"} {Math.abs(value).toFixed(1)}%
    </span>
  );
}

interface Props {
  restaurantSlug: string;
  mesSel: { anio: number; mm: number } | null;
}

export default function EstadoResultadosTable({ restaurantSlug, mesSel }: Props) {
  const [data, setData] = useState<ResumenERResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!restaurantSlug || !mesSel) return;
    let cancelled = false;
    setLoading(true);
    apiFetch(
      `/api/financiero/estado-resultados-resumen?restaurant_id=${restaurantSlug}&anio=${mesSel.anio}&mm=${mesSel.mm}`
    )
      .then((res) => {
        if (!res.ok) {
          if (!cancelled) {
            setError(`HTTP ${res.status}`);
            setLoading(false);
          }
          return;
        }
        return res.json();
      })
      .then((json) => {
        if (!cancelled) {
          setData(json);
          setLoading(false);
        }
      })
      .catch((e) => {
        if (!cancelled) {
          setError(String(e.message));
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [restaurantSlug, mesSel]);

  if (loading) {
    return (
      <Card className="!p-3">
        <Text className="text-tremor-content">Cargando...</Text>
      </Card>
    );
  }
  if (error || !data) {
    return (
      <Card className="!p-3">
        <Text className="text-tremor-content">Sin datos de estado de resultados.</Text>
      </Card>
    );
  }

  return (
    <Card className="!p-3">
      <div className="flex justify-between items-baseline flex-wrap gap-1">
        <Title className="!text-base">Estado de Resultados</Title>
        <Text className="!text-xs text-tremor-content">
          {data.mes_actual_label} vs {data.mes_anterior_label} · Acumulado del período
        </Text>
      </div>
      <table className="w-full text-xs">
        <thead>
          <tr className="text-left border-b border-tremor-border text-tremor-content">
            <th className="py-1.5 pr-2 font-medium">Concepto</th>
            <th className="py-1.5 px-2 text-right font-medium">{data.mes_actual_label}</th>
            <th className="hidden md:table-cell py-1.5 px-2 text-right font-medium">{data.mes_anterior_label}</th>
            <th className="py-1.5 px-2 text-right font-medium">Var. %</th>
            <th className="hidden sm:table-cell py-1.5 px-2 text-right font-medium">% Part. ventas</th>
            <th className="hidden md:table-cell py-1.5 px-2 text-right font-medium">Acumulado</th>
            <th className="hidden sm:table-cell py-1.5 pl-2 text-right font-medium">% Part. ventas Acu</th>
          </tr>
        </thead>
        <tbody>
          {data.rows.map((r) => {
            return (
              <tr key={r.concepto} className="border-b border-tremor-border/50">
                <td className="py-1.5 pr-2 font-medium text-tremor-content-emphasis">{r.concepto}</td>
                <td className="py-1.5 px-2 text-right tabular-nums font-semibold">{formatCurrency(r.mes_actual)}</td>
                <td className="hidden md:table-cell py-1.5 px-2 text-right tabular-nums text-tremor-content">{formatCurrency(r.mes_anterior)}</td>
                <td className="py-1.5 px-2 text-right"><DeltaBadge value={r.var_mes_pct} /></td>
                <td className="hidden sm:table-cell py-1.5 px-2 text-right tabular-nums text-tremor-content">{formatPct(r.pct_ventas)}</td>
                <td className="hidden md:table-cell py-1.5 px-2 text-right tabular-nums text-tremor-content">{formatCurrency(r.acumulado)}</td>
                <td className="hidden sm:table-cell py-1.5 pl-2 text-right tabular-nums text-tremor-content">{formatPct(r.pct_acumulado)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </Card>
  );
}